import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { CreateWithCriteriaButton } from '@src/features/ActionPaths/Creation/components/buttons/CreateWithCriteria';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { AggregationViewWidget } from '@src/widgets/RiskRecords';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import React from 'react';

/**
 * SearchResults
 *
 * Displays the search results for the Risk Analysis screen.
 *
 * @param assets
 * @param count
 * @param results
 * @param searchFilters
 * @returns {React.JSX.Element|null}
 * @constructor
 */
export function SearchResults({ assets = {}, count = 0, results = [], searchFilters = {} }) {
  const showRiskRecordsWidget = Array.isArray(searchFilters?.views) && searchFilters.views.includes('risk-records');
  const hasResults = !isEmpty(results);

  if (!hasResults && !showRiskRecordsWidget) {
    return null;
  }

  return (
    <ErrorHandler location="src/features/RiskAnalysis/components/SearchResults/SearchResults.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid css={{ margin: 0, padding: 0, width: '100%' }}>
          <Grid.Col
            css={{ paddingBottom: 'var(--abyss-space-lg)', paddingTop: 'var(--abyss-space-lg)' }}
            span={{ xs: '100%' }}
          >
            <Layout.Group space={Number(themeConfiguration?.theme?.space?.lg) || 24}>
              <Layout.Group>
                <Text fontWeight="bold">Total Records:</Text>
                <Badge outline variant="info">
                  {Number(count || 0).toLocaleString('en-US')}
                </Badge>
              </Layout.Group>
              <div>
                <CreateWithCriteriaButton criteria={searchFilters}>Draft Action Path</CreateWithCriteriaButton>
              </div>
            </Layout.Group>
          </Grid.Col>
          <Grid.Col css={{ margin: 0, padding: 0 }} span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
            <Flex
              alignContent="flex-start"
              alignItems="stretch"
              css={{ width: '100%' }}
              direction="row"
              justify="flex-start"
            >
              <React.Fragment>
                {hasResults &&
                  results.map((aggregation) => {
                    const view = assets?.ListViews?.data?.find((theView) => {
                      return theView?.name === aggregation?.researchAggregation?.name;
                    });

                    return (
                      <AggregationViewWidget
                        aggregation={aggregation}
                        assets={assets}
                        filters={searchFilters?.filters}
                        key={aggregation?.researchAggregation?.id}
                        view={view}
                      />
                    );
                  })}
                {showRiskRecordsWidget && (
                  <AggregationViewWidget
                    assets={assets}
                    criteria={searchFilters?.criteria}
                    filters={searchFilters?.filters}
                    key="ListRiskRecords"
                    view={{
                      description: 'Sample risk records that match filter criteria.',
                      id: 'risk-records',
                      name: 'Risk Records Sample Data',
                    }}
                  />
                )}
              </React.Fragment>
            </Flex>
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
